import {
  Accordion,
  Css,
  IconButton,
  NavLink,
  Palette,
  ScrollableContent,
  ScrollableParent,
  useBreakpoint,
  useTestIds,
} from "@homebound/beam";
import { camelCase } from "change-case";
import React, { Dispatch, ReactNode, SetStateAction, useEffect } from "react";
import { Route, matchPath } from "react-router";
import { useLocation } from "react-router-dom";
import { useSideNavContext } from "src/components/layout/SideNavContext";
import { getStage } from "src/context";
import { FeatureFlagHook, useFeatureFlags } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { topNavHeight } from "src/routes/layout/GlobalNav";
import { useLocalStorage } from "usehooks-ts";

export enum SideNavDisplayStyle {
  Default,
  Collapsible,
}

export type SideNavProps = {
  top?: ReactNode;
  sideNavConfig: SideNavAndRouterConfig[];
  contrast?: boolean;
  navWidth?: number;
  displayStyle?: SideNavDisplayStyle;
  /** Used to persist expanded/collapsed section states across different instances of the nav */
  storagekey?: string;
};

export function SideNav(props: SideNavProps) {
  const {
    top,
    sideNavConfig,
    contrast = false,
    displayStyle = SideNavDisplayStyle.Default,
    navWidth = displayStyle === SideNavDisplayStyle.Collapsible ? 260 : 220,
    storagekey = "sideNav",
  } = props;
  const { sideNavState, setSideNavState } = useSideNavContext();
  const bp = useBreakpoint();
  const tid = useTestIds({}, "sideNav");
  const location = useLocation();
  const { featureIsEnabled } = useFeatureFlags();

  const navToggleSize = 24;
  const navToggleLeft = navWidth - navToggleSize - 20;
  const closedNavLeft = sideNavCollapsedWidth - navWidth;
  const navExpanded = sideNavState === "expanded";
  const navHidden = sideNavState === "hidden";
  // Store expanded collapsible sections using localStorage to persist state.
  // Expand by default sections that match the current location path
  const defaultExpandedMap: Record<string, boolean> = Object.fromEntries(
    sideNavConfig.map((s) => {
      const shouldExpand = s.items.some(({ path }) => !!matchPath(location.pathname, { path }));
      return [s.title, shouldExpand];
    }),
  );
  const [expandedSections, setExpandedSections] = useLocalStorage<Record<string, boolean>>(
    `${storagekey}ExpandedSections`,
    defaultExpandedMap,
  );

  // Smartly auto-close the side nav on smaller screens when switching between routes / location changes.
  useEffect(() => {
    if (navExpanded && bp.mdAndDown) {
      setSideNavState("collapse");
    }
    // Explicitly not including `navExpanded` as we don't want to immediately close the nav if someone just opened it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, bp.mdAndDown, setSideNavState]);
  if (navHidden) return null;

  const { bgColor, bColor, iconColor } = getContrastStyles(contrast);

  // Separate items/sections that are pinned to the bottom of the nav
  const [pinnedSections, mainSections] = sideNavConfig.partition((s) => !!s.pinnedToBottom);

  return (
    <div
      css={{
        ...Css.df.relative.fdc
          .bgColor(bgColor)
          .bc(bColor)
          .transition.br.fg0.fs0.z999.wPx(navWidth)
          .if(!navExpanded)
          .mlPx(closedNavLeft).$,
        ...(bp.mdAndDown ? Css.absolute.h(`calc(100% - ${topNavHeight}px)`).$ : {}),
      }}
      {...tid}
    >
      <ScrollableParent>
        <div css={Css.relative.$}>
          <div css={Css.absolute.top3.leftPx(navToggleLeft).df.aic.jcc.$}>
            <IconButton
              icon={navExpanded ? "menuClose" : "menuOpen"}
              onClick={() =>
                setSideNavState((prevState) =>
                  prevState === "expanded" ? ("collapse" as const) : ("expanded" as const),
                )
              }
              color={iconColor}
              contrast={contrast}
              {...tid.toggle}
            />
          </div>
          {navExpanded && (
            <>
              {top}
              {mainSections.nonEmpty && (
                <ScrollableContent>
                  {displayStyle === SideNavDisplayStyle.Default
                    ? RenderDefaultSections(mainSections, contrast, featureIsEnabled, !top)
                    : RenderCollapsibleSections(
                        mainSections,
                        contrast,
                        featureIsEnabled,
                        expandedSections,
                        setExpandedSections,
                      )}
                </ScrollableContent>
              )}
            </>
          )}
        </div>
      </ScrollableParent>
      {pinnedSections.nonEmpty && (
        <div css={Css.py2.bt.bc(bColor).$} {...tid.pinnedToBottom}>
          {displayStyle === SideNavDisplayStyle.Default
            ? RenderDefaultSections(pinnedSections, contrast, featureIsEnabled)
            : RenderCollapsibleSections(
                pinnedSections,
                contrast,
                featureIsEnabled,
                expandedSections,
                setExpandedSections,
              )}
        </div>
      )}
    </div>
  );
}

const RenderSideNavItems = (
  items: SideNavAndRouterConfigItem[],
  featureIsEnabled: (flag: FeatureFlagType) => boolean,
  opts: { contrast: boolean; collapsible?: boolean },
) => {
  const tid = useTestIds({}, "sideNavItem");
  const location = useLocation();
  const { contrast, collapsible } = opts;

  return items
    .filter(({ featureFlag }) => !featureFlag || featureIsEnabled(featureFlag))
    .filter(({ hideInProd }) => !(getStage() === "prod" && hideInProd))
    .map(({ label, href, path, isExternal }) => (
      <li key={href ?? label}>
        <NavLink
          label={collapsible ? <div css={Css.pl2.$}>{label}</div> : label}
          variant="side"
          contrast={contrast}
          href={href}
          active={path ? !!matchPath(location.pathname, { path }) : false}
          icon={isExternal ? "linkExternal" : undefined}
          {...tid[camelCase(label)]}
        />
      </li>
    ));
};

const RenderDefaultSections = (
  sections: SideNavAndRouterConfig[],
  contrast: boolean,
  featureIsEnabled: (flag: FeatureFlagType) => boolean,
  paddingTop?: boolean,
) => {
  const { titleColor, bColor } = getContrastStyles(contrast);
  return (
    <nav>
      <ul css={Css.listReset.df.fdc.gap2.if(!!paddingTop).pt8.$}>
        {sections.map(({ title, items }, idx) => (
          <li key={title ?? idx} css={Css.px2.pt2.bt.bc(bColor).addIn(":first-of-type", Css.pt0.bn.$).$}>
            {title && <h3 css={Css.xs2Sb.px1.pbPx(2).color(titleColor).ttu.$}>{title}</h3>}
            <ul css={Css.listReset.$}>{RenderSideNavItems(items, featureIsEnabled, { contrast })}</ul>
          </li>
        ))}
      </ul>
    </nav>
  );
};

/** Sections with multiple items are collapsible, while single items are rendered as standalone sections. */
const RenderCollapsibleSections = (
  sections: SideNavAndRouterConfig[],
  contrast: boolean,
  featureIsEnabled: (flag: FeatureFlagType) => boolean,
  expandedSections: Record<string, boolean>,
  setExpandedSections: Dispatch<SetStateAction<Record<string, boolean>>>,
) => {
  const tid = useTestIds({}, "sideNavSection");

  return (
    <nav>
      <ul css={Css.listReset.df.fdc.$}>
        {sections.map(({ title, items }, index) => {
          return (
            <li key={title ?? index} css={Css.px2.$}>
              {items.length > 1 ? (
                <Accordion
                  omitPadding
                  onToggle={() => {
                    if (!title) return;
                    // Toggle the section open/closed matching by section title, since index may vary accross projects
                    setExpandedSections((prev) => ({ ...prev, [title]: !prev[title] }));
                  }}
                  topBorder={false}
                  defaultExpanded={!!title && expandedSections?.[title]}
                  title={<div css={Css.gray700.smSb.$}>{title}</div>}
                  xss={Css.px1.pyPx(5).$}
                  {...tid[camelCase(title ?? "collapsibleSection")]}
                >
                  <ul css={Css.listReset.$}>
                    {RenderSideNavItems(items, featureIsEnabled, { contrast, collapsible: true })}
                  </ul>
                </Accordion>
              ) : (
                <ul css={Css.listReset.$}>{RenderSideNavItems(items, featureIsEnabled, { contrast })}</ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

const getContrastStyles = (contrast: boolean) =>
  contrast
    ? { bgColor: Palette.Gray900, bColor: Palette.Gray800, iconColor: Palette.Gray500, titleColor: Palette.Gray400 }
    : { bgColor: Palette.White, bColor: Palette.Gray200, iconColor: Palette.Gray800, titleColor: Palette.Gray900 };

export type SideNavAndRouterConfig = {
  title?: string;
  items: SideNavAndRouterConfigItem[];
  /** Pins the section to the bottom of the nav */
  pinnedToBottom?: boolean;
};

export type SideNavAndRouterConfigItem = {
  /** Will prevent sidebar links from being generated in production, but you should still be able to URL-hack to it */
  hideInProd?: boolean;
  label: string;
  href: string;
  /** Used to match against the URL to denote this nav link item as "active" and when creating the <Route /> component
   * It is optional for cases where the `href` links outside the application (e.g. `isExternal: true`)
   * If not defined, a <Route /> component will not be created. */
  path?: string | string[];
  isExternal?: boolean;
  /** Notifies <Route /> path is exact. This is only used when creating `<Route />` via the SideNavConfigItems */
  routeIsExact?: true;
  /** Passed into <Route component={component} />.
   * This is only used when creating `<Route />` via the SideNavConfigItems
   * If not defined, a <Route /> component will not be created and will need to be explicitly defined to handle the route. */
  component?: React.ComponentProps<typeof Route>["component"];
  featureFlag?: FeatureFlagType;
};

export function navLinksToRoutes(
  config: SideNavAndRouterConfig[],
  featureIsEnabled?: FeatureFlagHook["featureIsEnabled"],
): React.ReactNode[] {
  return (
    config
      .flatMap(({ items }) => items)
      // filter out items that don't have a component and path defined.
      .filter(({ component, path }) => !!component && !!path)
      .filter(({ featureFlag }) => !featureFlag || (featureIsEnabled && featureIsEnabled(featureFlag)))
      .map(({ routeIsExact, path, component }: SideNavAndRouterConfigItem) => (
        <Route key={Array.isArray(path) ? path.join() : path} path={path} exact={routeIsExact} component={component} />
      ))
  );
}

export const sideNavCollapsedWidth = 56;
